import React, { useState, useEffect } from 'react';
import { updateConnection } from '../lib/supabase';
import { FiEdit2, FiSave, FiX, FiTag, FiFileText, FiMapPin, FiCalendar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const ConnectionDetails = ({ connection, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(connection?.notes || '');
  const [tags, setTags] = useState(connection?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [meetingContext, setMeetingContext] = useState({
    event: connection?.meeting_context?.event || '',
    location: connection?.meeting_context?.location || '',
    date: connection?.meeting_context?.date || '',
  });
  const [saving, setSaving] = useState(false);

  const commonTags = ['Event', 'Conference', 'Networking', 'Client', 'Partner', 'Friend', 'Colleague'];

  useEffect(() => {
    if (connection) {
      setNotes(connection.notes || '');
      setTags(connection.tags || []);
      setMeetingContext(connection.meeting_context || { event: '', location: '', date: '' });
    }
  }, [connection]);

  const handleAddTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await updateConnection(connection.id, {
        notes: notes || null,
        tags: tags.length > 0 ? tags : null,
        meeting_context: meetingContext.event || meetingContext.location || meetingContext.date 
          ? meetingContext 
          : null,
      });

      if (error) {
        throw error;
      }

      toast.success('Connection updated successfully!');
      setIsEditing(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating connection:', error);
      toast.error(`Failed to update: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(connection?.notes || '');
    setTags(connection?.tags || []);
    setMeetingContext(connection?.meeting_context || { event: '', location: '', date: '' });
    setIsEditing(false);
  };

  if (!connection) {
    return null;
  }

  return (
    <div className="glass-dark p-6 rounded-xl border border-dark-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-lime-bright">Connection Details</h3>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="text-dark-400 hover:text-lime-400 transition-colors"
          >
            <SafeIcon icon={FiEdit2} />
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-lime-400 hover:text-lime-300 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} />
            </button>
            <button
              onClick={handleCancel}
              className="text-dark-400 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-dark-200 mb-2 flex items-center">
          <SafeIcon icon={FiFileText} className="mr-2" />
          Notes
        </label>
        {isEditing ? (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700 resize-none"
            placeholder="Add notes about this connection..."
          />
        ) : (
          <p className="text-dark-300 text-sm min-h-[3rem]">
            {notes || <span className="text-dark-500 italic">No notes</span>}
          </p>
        )}
      </div>

      {/* Tags */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-dark-200 mb-2 flex items-center">
          <SafeIcon icon={FiTag} className="mr-2" />
          Tags
        </label>
        {isEditing ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 bg-lime-500/20 text-lime-400 rounded-full text-sm border border-lime-500/50"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-lime-400 hover:text-lime-300"
                  >
                    <SafeIcon icon={FiX} className="text-xs" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag(newTag);
                  }
                }}
                placeholder="Add tag..."
                className="flex-1 px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              />
              <button
                onClick={() => handleAddTag(newTag)}
                className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {commonTags
                .filter(tag => !tags.includes(tag))
                .map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleAddTag(tag)}
                    className="px-3 py-1 bg-dark-800 text-dark-300 rounded-full text-sm border border-dark-700 hover:border-lime-500/50 hover:text-lime-400 transition-colors"
                  >
                    + {tag}
                  </button>
                ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.length > 0 ? (
              tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 bg-lime-500/20 text-lime-400 rounded-full text-sm border border-lime-500/50"
                >
                  {tag}
                </span>
              ))
            ) : (
              <span className="text-dark-500 italic text-sm">No tags</span>
            )}
          </div>
        )}
      </div>

      {/* Meeting Context */}
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2 flex items-center">
          <SafeIcon icon={FiMapPin} className="mr-2" />
          Meeting Context
        </label>
        {isEditing ? (
          <div className="space-y-3">
            <input
              type="text"
              value={meetingContext.event}
              onChange={(e) => setMeetingContext({ ...meetingContext, event: e.target.value })}
              placeholder="Event name (e.g., Web3 Conference)"
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
            />
            <input
              type="text"
              value={meetingContext.location}
              onChange={(e) => setMeetingContext({ ...meetingContext, location: e.target.value })}
              placeholder="Location (e.g., San Francisco, CA)"
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
            />
            <input
              type="date"
              value={meetingContext.date}
              onChange={(e) => setMeetingContext({ ...meetingContext, date: e.target.value })}
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
            />
          </div>
        ) : (
          <div className="space-y-1 text-sm text-dark-300">
            {meetingContext.event && (
              <p><span className="text-dark-400">Event:</span> {meetingContext.event}</p>
            )}
            {meetingContext.location && (
              <p><span className="text-dark-400">Location:</span> {meetingContext.location}</p>
            )}
            {meetingContext.date && (
              <p><span className="text-dark-400">Date:</span> {new Date(meetingContext.date).toLocaleDateString()}</p>
            )}
            {!meetingContext.event && !meetingContext.location && !meetingContext.date && (
              <span className="text-dark-500 italic">No meeting context</span>
            )}
          </div>
        )}
      </div>

      {isEditing && (
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConnectionDetails;

